/**
 * The **connection editor** — the second shared egress/ingress form pattern
 * (storyboard ``shared/form-patterns.md``; ADR-036 §D-1 / §D-7).
 *
 * Edits one Consumer connection belonging to an interface: direction, endpoint,
 * port, timeout, bounded retry, per-connection state. An interface may hold
 * several (the CDS repeat-endpoint fan-out, generalized), so this renders one
 * card per connection with add / remove above it.
 *
 * # Three behaviours worth naming
 *
 * **Direction rewrites the endpoint field, it does not just hide it.** The
 * config model rejects a host on a ``pull`` connection outright, because a host
 * that would be silently ignored is how a connection ends up pointing somewhere
 * nobody intended. Switching to ``pull`` therefore clears the host rather than
 * stashing it — what the form shows is what will be sent.
 *
 * **Retry is bounded, and the form says why.** An endlessly-retried message is a
 * stalled interface, not resilience; the bound is what turns a dead Consumer
 * into a quarantined message instead of a wedged pipeline.
 *
 * **Start/stop is not part of the form.** A connection's state is flipped
 * through its own endpoint so it lands as its own audited event
 * (REQ-PEER-044) and takes effect without a whole-document save. The editor
 * *displays* the state and offers the control; the control does not travel with
 * the Save button.
 *
 * Cross-references:
 *   - ADR-036 §D-1 (interface model); ADR-017 §8 (pull is the default direction).
 *   - REQ-PEER-041 (fail-safe stopped), -043 (privileged), -044 (audited).
 *   - Flow W §4 (configure connections); ``shared/form-patterns.md`` §1.
 */
import { useState } from "react";
import {
  describePublishedRange,
  describeUnpublishedPort,
  portOutsidePublishedRange,
  type PublishedPortRange,
} from "@/lib/listen-port-range";
import { CONNECTION_DIRECTIONS, type ConnectionRole } from "@/peers/peer-catalog";
import { PeerNumberInput, PeerSelect, PeerTextInput } from "@/peers/peer-form-primitives";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import type { EffectiveState } from "@/peers/peer-effective-state";
import type { PeerInterfaceConnectionConfig } from "@/peers/peer-model";

/** Props for {@link PeerInterfaceConnectionEditor}. */
export interface PeerInterfaceConnectionEditorProps {
  connection: PeerInterfaceConnectionConfig;
  /** Index within the interface, for stable test ids and headings. */
  index: number;
  onChange: (next: PeerInterfaceConnectionConfig) => void;
  /**
   * Drop this connection from the **draft**. Applied by the next Save, undone by
   * Cancel — so it needs no confirmation.
   */
  onRemove: () => void;
  /**
   * Delete this connection **on the server**, immediately (REQ-PEER-105,
   * REQ-UI-154). Supplied only for a connection that is already persisted; a
   * draft-only row has nothing to delete and gets {@link onRemove} instead.
   *
   * ⚠ **Distinct from `onToggleState`, and worded apart from it.** Stopping
   * silences this connection and keeps it, so starting the interface brings it
   * back; deleting removes it from the configuration and it does not come back.
   * Two controls that read alike is how an operator reaching for a kill switch
   * decommissions a path instead — which is why this one confirms and stop does
   * not.
   */
  onDelete?: () => void;
  /** Flip this connection's state through its own endpoint (not the Save button). */
  onToggleState?: (next: "started" | "stopped") => void;
  /** True while a start/stop for this connection is in flight. */
  statePending?: boolean;
  /** Validation message for the connection's name (duplicate within the interface). */
  nameError?: string | null;
  /** True while the interface has never been saved — state control is not yet meaningful. */
  unsaved?: boolean;
  /**
   * This connection resolved against its peer and interface (REQ-UI-144).
   *
   * Rendered beside the state pill, never instead of it: a `started` connection
   * under a stopped interface is not emitting, and the pill alone would say it
   * is.
   */
  suppression?: EffectiveState;
  /**
   * The peer listener range the deployment publishes to the host
   * (REQ-DEPLOY-054), from ``GET /api/config``. A ``pull`` port outside it is
   * refused inline with the BFF's own wording (REQ-UI-198); ``null`` /
   * ``undefined`` means the range is not advertised, so nothing is refused
   * here and the hint says so. Irrelevant to ``dial_out``, whose port is the
   * Consumer's.
   */
  publishedRange?: PublishedPortRange | null;
}

/** Render the editor card for one Consumer connection. */
export function PeerInterfaceConnectionEditor({
  connection,
  index,
  onChange,
  onRemove,
  onDelete,
  onToggleState,
  statePending = false,
  nameError,
  unsaved = false,
  suppression,
  publishedRange,
}: PeerInterfaceConnectionEditorProps): React.ReactElement {
  // Held per connection rather than lifted: two open confirmations on one
  // interface would be two armed deletes with no way to tell which is which.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dialOut = connection.role === "dial_out";
  // ``pull`` binds locally (ADR-039: Meridian binds, the Consumer connects),
  // so it must sit inside the published range; the BFF refuses it on save
  // regardless of the connection's state, and so does this (REQ-UI-198;
  // REQ-PEER-114).
  const portError =
    !dialOut &&
    publishedRange != null &&
    portOutsidePublishedRange(connection.endpoint_port, publishedRange)
      ? describeUnpublishedPort(connection.endpoint_port, publishedRange, "peer")
      : null;
  const set = (patch: Partial<PeerInterfaceConnectionConfig>): void =>
    onChange({ ...connection, ...patch });

  const setDirection = (value: string): void => {
    const role = value as ConnectionRole;
    // Clearing rather than stashing: the config model rejects a host on `pull`,
    // so a hidden-but-retained host would be an invisible 422 waiting to fire.
    set({ role, endpoint_host: role === "dial_out" ? connection.endpoint_host : null });
  };

  return (
    <section
      data-testid={`peer-connection-${index}`}
      aria-label={connection.name === "" ? `Connection ${index + 1}` : connection.name}
      className="rounded border border-border bg-bg-base p-3"
    >
      <header className="flex flex-wrap items-center gap-2 pb-2">
        <h4 className="text-body-sm font-medium text-text-primary">
          {connection.name === "" ? `Connection ${index + 1}` : connection.name}
        </h4>
        <StatePill state={connection.state} />
        {suppression && (
          <PeerSuppressionNote resolved={suppression} testId={`connection-${index}`} />
        )}
        <div className="ml-auto flex items-center gap-1">
          {onToggleState && !unsaved && (
            <StateToggleButton
              state={connection.state}
              pending={statePending}
              onToggle={onToggleState}
              testId={`peer-connection-${index}-toggle`}
              label="connection"
            />
          )}
          {onDelete === undefined ? (
            // Draft-only: nothing on the server to delete, and Cancel undoes it.
            <button
              type="button"
              onClick={onRemove}
              data-testid={`peer-connection-${index}-remove`}
              className="rounded border border-border px-2 py-0.5 text-meta text-text-secondary hover:border-destructive hover:text-destructive"
            >
              Remove
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid={`peer-connection-${index}-delete`}
              className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
            >
              Delete&hellip;
            </button>
          )}
        </div>
      </header>

      {/* The confirmation NAMES what it does and what it does not (REQ-UI-154).
          "This is not the same as stopping it" is the load-bearing clause: the
          two controls sit side by side, and an operator who conflated them would
          decommission a path expecting to silence it. */}
      {confirmDelete && onDelete !== undefined && (
        <div
          role="alert"
          data-testid={`peer-connection-${index}-delete-confirm`}
          className="mb-2 flex flex-wrap items-center gap-2 rounded border border-destructive px-2 py-1 text-meta text-destructive"
        >
          <span className="flex-1">
            Delete connection <strong>{connection.name}</strong> permanently? Data queued for it is
            not redirected. This is not the same as stopping it.
          </span>
          <button
            type="button"
            onClick={() => {
              setConfirmDelete(false);
              onDelete();
            }}
            data-testid={`peer-connection-${index}-delete-go`}
            className="rounded bg-destructive px-2 py-0.5 text-destructive-foreground hover:opacity-90"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            data-testid={`peer-connection-${index}-delete-cancel`}
            className="rounded border border-border px-2 py-0.5 text-text-primary hover:bg-bg-elevated"
          >
            Keep
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <PeerTextInput
          label="Name"
          value={connection.name}
          maxLength={128}
          onChange={(name) => set({ name })}
          error={nameError}
          testId={`peer-connection-${index}-name`}
          placeholder="north-ridge-fusion-primary"
        />
        <PeerSelect
          label="Direction"
          value={connection.role}
          testId={`peer-connection-${index}-direction`}
          options={CONNECTION_DIRECTIONS.map((d) => ({ value: d.value, label: d.label }))}
          onChange={setDirection}
          hint={CONNECTION_DIRECTIONS.find((d) => d.value === connection.role)?.hint}
        />
        {dialOut ? (
          <PeerTextInput
            label="Consumer host"
            value={connection.endpoint_host ?? ""}
            mono
            onChange={(endpoint_host) => set({ endpoint_host })}
            testId={`peer-connection-${index}-host`}
            placeholder="consumer.range.example"
            hint="Required when Meridian dials out."
          />
        ) : (
          <PeerTextInput
            label="Consumer host"
            value=""
            disabled
            onChange={() => undefined}
            testId={`peer-connection-${index}-host`}
            hint="Not used — Meridian binds locally and the Consumer connects."
          />
        )}
        <PeerNumberInput
          label={dialOut ? "Destination port" : "Local bind port"}
          value={connection.endpoint_port}
          min={1}
          max={65535}
          testId={`peer-connection-${index}-port`}
          onChange={(endpoint_port) => set({ endpoint_port })}
          error={portError}
          hint={
            dialOut
              ? "The port on the Consumer's host."
              : `The port on this host. ${describePublishedRange(publishedRange, "peer")}`
          }
        />
        <PeerNumberInput
          label="Timeout (seconds)"
          value={connection.timeout_seconds}
          min={0.1}
          max={3600}
          step="any"
          testId={`peer-connection-${index}-timeout`}
          onChange={(timeout_seconds) => set({ timeout_seconds })}
          hint="Connect and write timeout."
        />
        <PeerNumberInput
          label="Retry attempts"
          value={connection.retry?.max_attempts ?? 3}
          min={1}
          max={100}
          testId={`peer-connection-${index}-retry-attempts`}
          onChange={(max_attempts) =>
            set({
              retry: { max_attempts, backoff_seconds: connection.retry?.backoff_seconds ?? 1 },
            })
          }
          hint="Bounded, never infinite — after this the message is quarantined."
        />
        <PeerNumberInput
          label="Retry backoff (seconds)"
          value={connection.retry?.backoff_seconds ?? 1}
          min={0.1}
          max={3600}
          step="any"
          testId={`peer-connection-${index}-retry-backoff`}
          onChange={(backoff_seconds) =>
            set({ retry: { max_attempts: connection.retry?.max_attempts ?? 3, backoff_seconds } })
          }
        />
      </div>
    </section>
  );
}
